import {useContext, useEffect, useMemo, useRef, useState} from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import {
    GoogleBigQueryRecipeValidationDataStatusEnum,
    GoogleDataProviderResourceDataBigQueryRecipeStatusEnum,
    ProjectIntegrationsApi,
} from "../../../../api/authority/generated/index";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import Button from "../../../../components/Button.jsx";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import Spinner from "../../../../components/Spinner.jsx";
import FormError from "../../../../components/form/FormError.jsx";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {continueGoogleDataOAuth} from "./googleDataOAuthNavigation.js";
import {IntegrationActions, IntegrationConnectButton, IntegrationSection, OAuthConnectionStatus} from "../IntegrationDetailUi.jsx";
import googleLogo from "../../../../images/login/g.svg";
import {
    analyticsPropertySelectOptions, bigQueryDatasetSelectOptions, bigQueryProjectSelectOptions,
    bigQueryDatasetKey, bigQueryMixedSelectionTranslationValues, bigQueryRecipeSaveOutcome, bytesToMegabytes, formatMegabytesInput, formatProcessedBytes, hydrateBigQueryDraft, hydrateBigQueryPropertyTimeZones, hydrateBigQuerySelection, megabytesToBytes,
    loadGoogleIntegrationUsage, reconcileBigQueryDraftWithDiscovery, reloadIntegrationDetailWithDraftCleanup, revalidatePendingBigQueryRecipes, selectedBigQueryRecipeInputs, summarizeBigQueryRecipeValidationResults, withDefaultBigQueryPropertyTimeZones,
} from "./googleDataSelection.js";

const ResourceLoadingNotice = ({children}) => <div
    className={'mb-3 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-800 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-200'}
    role={'status'}
    aria-live={'polite'}
>
    <span className={'h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600 dark:border-violet-800 dark:border-t-violet-300'} aria-hidden={'true'}/>
    <span>{children}</span>
</div>;

const timeZoneOptions = moment.tz.names().map((timeZone) => ({value: timeZone, label: timeZone}));

export default function GoogleDataIntegrationForm({project, projectIntegration, brand, reloadIntegrationDetail}) {
    const {getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const {t, i18n} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [resourceLoading, setResourceLoading] = useState(false);
    const [error, setError] = useState(null);
    const storedResources = projectIntegration?.providerResources ?? [];
    const availableResources = projectIntegration?.availableProviderResources?.length > 0
        ? projectIntegration.availableProviderResources
        : storedResources;
    const [merchantAccounts, setMerchantAccounts] = useState(() => brand.googleDataType === 'merchant'
        ? storedResources.map((resource) => ({accountId: resource.providerId, displayName: resource.displayName, enabled: true, testAccount: false}))
        : []);
    const [selectedAccounts, setSelectedAccounts] = useState(() => brand.googleDataType === 'merchant'
        ? storedResources.map((resource) => resource.providerId)
        : []);
    const [analyticsProperties, setAnalyticsProperties] = useState(() => brand.googleDataType === 'analytics'
        ? storedResources.map((resource) => ({
            propertyId: resource.providerId,
            displayName: resource.displayName,
            accountDisplayName: resource.parentDisplayName,
            timeZone: resource.timeZone,
            currencyCode: resource.currencyCode,
            enabled: true,
            demographicsEnabled: false,
            persistedSelection: true,
        }))
        : []);
    const [selectedProperties, setSelectedProperties] = useState(() => brand.googleDataType === 'analytics'
        ? storedResources.map((resource) => resource.providerId)
        : []);
    const [searchConsoleSites, setSearchConsoleSites] = useState(() => brand.googleDataType === 'search-console'
        ? availableResources.map((resource) => ({
            siteUrl: resource.providerId,
            permissionLevel: 'siteOwner',
            projectDomainVerified: true,
            enabled: storedResources.some((selectedResource) => selectedResource.providerId === resource.providerId),
            persistedSelection: storedResources.some((selectedResource) => selectedResource.providerId === resource.providerId),
        }))
        : []);
    const [selectedSites, setSelectedSites] = useState(() => brand.googleDataType === 'search-console'
        ? storedResources.map((resource) => resource.providerId)
        : []);
    const storedBigQueryResources = brand.googleDataType === 'bigquery' ? storedResources : [];
    const [bigQueryProjects, setBigQueryProjects] = useState(() => [...new Set(storedBigQueryResources.map((resource) => resource.parentDisplayName).filter(Boolean))].map((projectId) => ({projectId, displayName: projectId})));
    const [selectedBigQueryProjects, setSelectedBigQueryProjects] = useState(() => [...new Set(storedResources.map((resource) => resource.parentDisplayName).filter(Boolean))]);
    const [bigQueryDatasets, setBigQueryDatasets] = useState(() => hydrateBigQuerySelection([], [], storedBigQueryResources).datasets);
    const [selectedBigQueryDatasets, setSelectedBigQueryDatasets] = useState(() => storedResources.map((resource) => `${resource.parentDisplayName}:${resource.providerId}`));
    const [bigQueryPropertyTimeZones, setBigQueryPropertyTimeZones] = useState(() => hydrateBigQueryPropertyTimeZones(storedBigQueryResources));
    const [maximumMegabytesBilled, setMaximumMegabytesBilled] = useState('1 000');
    const [dailyMegabytesLimit, setDailyMegabytesLimit] = useState('10 000');
    const [monthlyMegabytesLimit, setMonthlyMegabytesLimit] = useState('200 000');
    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const automaticDiscoveryStarted = useRef(false);
    const automaticInstallStarted = useRef(false);
    const integrationDetailHydrationStarted = useRef(false);
    const bigQueryDraftOnNextHydration = useRef(null);
    const api = useMemo(
        () => new ProjectIntegrationsApi(getAuthorityConfiguration()),
        [getAuthorityConfiguration],
    );

    useEffect(() => {
        if (!projectIntegration) return;

        const selectedResources = projectIntegration.providerResources ?? [];
        const offeredResources = projectIntegration.availableProviderResources?.length > 0
            ? projectIntegration.availableProviderResources
            : selectedResources;
        const selectedIds = selectedResources.map((resource) => resource.providerId);

        if (brand.googleDataType === 'merchant') {
            setMerchantAccounts(offeredResources.map((resource) => ({
                accountId: resource.providerId,
                displayName: resource.displayName,
                enabled: selectedIds.includes(resource.providerId),
                testAccount: false,
            })));
            setSelectedAccounts(selectedIds);
        }

        if (brand.googleDataType === 'analytics') {
            setAnalyticsProperties((currentProperties) => offeredResources.map((resource) => {
                const currentProperty = currentProperties.find((property) => property.propertyId === resource.providerId);
                return {
                    propertyId: resource.providerId,
                    displayName: resource.displayName,
                    accountDisplayName: resource.parentDisplayName,
                    timeZone: resource.timeZone,
                    currencyCode: resource.currencyCode,
                    enabled: selectedIds.includes(resource.providerId),
                    demographicsEnabled: currentProperty?.demographicsEnabled ?? false,
                    persistedSelection: selectedIds.includes(resource.providerId),
                };
            }));
            setSelectedProperties(selectedIds);
        }

        if (brand.googleDataType === 'search-console') {
            setSearchConsoleSites(offeredResources.map((resource) => ({
                siteUrl: resource.providerId,
                permissionLevel: 'siteOwner',
                projectDomainVerified: true,
                enabled: selectedIds.includes(resource.providerId),
                persistedSelection: selectedIds.includes(resource.providerId),
            })));
            setSelectedSites(selectedIds);
        }

        if (brand.googleDataType === 'bigquery') {
            const draft = hydrateBigQueryDraft(selectedResources, bigQueryDraftOnNextHydration.current);
            bigQueryDraftOnNextHydration.current = null;
            const storedBudget = selectedResources.find((resource) => resource.maximumBytesBilled != null);
            setBigQueryProjects((current) => hydrateBigQuerySelection(current, [], selectedResources).projects);
            setBigQueryDatasets((current) => hydrateBigQuerySelection([], current, selectedResources).datasets);
            setSelectedBigQueryProjects(draft.selectedProjects);
            setSelectedBigQueryDatasets(draft.selectedDatasets);
            setBigQueryPropertyTimeZones(draft.propertyTimeZones);
            if (storedBudget) {
                setMaximumMegabytesBilled(bytesToMegabytes(storedBudget.maximumBytesBilled, '1000'));
                setDailyMegabytesLimit(bytesToMegabytes(storedBudget.dailyBytesLimit, '10000'));
                setMonthlyMegabytesLimit(bytesToMegabytes(storedBudget.monthlyBytesLimit, '200000'));
            }
        }
    }, [projectIntegration, brand.googleDataType]);

    const withError = async (action) => {
        setLoading(true);
        setError(null);
        try {
            return await action();
        } catch (caught) {
            setError(caught);
            const message = await et(caught);
            if (message) toast.error(message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const withResourceError = async (action) => {
        setResourceLoading(true);
        setError(null);
        try {
            return await action();
        } catch (caught) {
            setError(caught);
            const message = await et(caught);
            if (message) toast.error(message);
            return null;
        } finally {
            setResourceLoading(false);
        }
    };

    useEffect(() => {
        const providerResourcesMissing = projectIntegration?.providerResources === undefined
            || projectIntegration?.availableProviderResources === undefined
            || (projectIntegration?.hasEnabledProviderResources && storedResources.length === 0);
        if (
            integrationDetailHydrationStarted.current
            || !projectIntegration?.authorizationSuccessful
            || !providerResourcesMissing
        ) {
            return;
        }

        integrationDetailHydrationStarted.current = true;
        void withError(reloadIntegrationDetail);
    }, [projectIntegration?.id, projectIntegration?.authorizationSuccessful, projectIntegration?.hasEnabledProviderResources]);

    const startOAuth = async (integration) => {
        const authorization = await api.startGoogleDataIntegrationOAuth({
            projectId: project.id,
            integrationId: integration.id,
        });
        if (!authorization.authorizationUrl) {
            throw new Error('Google OAuth authorization URL is unavailable.');
        }

        continueGoogleDataOAuth({
            authorizationUrl: authorization.authorizationUrl,
            integrationId: integration.id,
            projectId: project.id,
            resetCurrentUserProjects,
        });
    };

    const installAndConnect = () => withError(async () => {
        const authorization = await api.installGoogleDataIntegration({
            projectId: project.id,
            type: brand.googleDataType,
        });
        if (!authorization.authorizationUrl) {
            throw new Error('Google OAuth authorization URL is unavailable.');
        }
        continueGoogleDataOAuth({
            authorizationUrl: authorization.authorizationUrl,
            projectId: project.id,
            resetCurrentUserProjects,
        });
    });

    useEffect(() => {
        if (projectIntegration || automaticInstallStarted.current) return;
        automaticInstallStarted.current = true;
        void installAndConnect();
    }, [projectIntegration]);

    const reconnect = () => withError(() => startOAuth(projectIntegration));

    const discoverMerchantAccounts = () => withResourceError(async () => {
        const accounts = await api.discoverGoogleMerchantAccounts({
            projectId: project.id,
            integrationId: projectIntegration.id,
        });
        setMerchantAccounts(accounts);
    });

    const saveMerchantAccounts = () => withResourceError(async () => {
        await api.selectGoogleMerchantAccounts({
            projectId: project.id,
            integrationId: projectIntegration.id,
            requestBody: selectedAccounts,
        });
        await reloadIntegrationDetail();
        toast.success(t('google_merchant.accounts_saved', {defaultValue: 'Výběr Merchant účtů byl uložen.'}));
    });

    const discoverAnalyticsProperties = () => withResourceError(async () => {
        const properties = await api.discoverGoogleAnalyticsProperties({
            projectId: project.id,
            integrationId: projectIntegration.id,
        });
        setAnalyticsProperties(properties);
    });

    const saveAnalyticsProperties = () => withResourceError(async () => {
        await api.selectGoogleAnalyticsProperties({
            projectId: project.id,
            integrationId: projectIntegration.id,
            requestBody: selectedProperties,
        });
        await reloadIntegrationDetail();
        toast.success(t('google_analytics.properties_saved', {defaultValue: 'Výběr GA4 služeb byl uložen.'}));
    });

    const discoverSearchConsoleSites = () => withResourceError(async () => {
        const sites = await api.discoverGoogleSearchConsoleSites({
            projectId: project.id,
            integrationId: projectIntegration.id,
        });
        setSearchConsoleSites(sites);
        await reloadIntegrationDetail();
    });

    const saveSearchConsoleSites = () => withResourceError(async () => {
        await api.selectGoogleSearchConsoleSites({
            projectId: project.id,
            integrationId: projectIntegration.id,
            requestBody: selectedSites,
        });
        await reloadIntegrationDetail();
        toast.success(t('google_search_console.domains_saved', {defaultValue: 'Výběr domén byl uložen.'}));
    });

    const loadBigQueryDatasets = async (dataProjectIds) => {
        if (dataProjectIds.length === 0) {
            setBigQueryDatasets([]);
            return [];
        }
        const datasets = (await Promise.all(dataProjectIds.map(async (dataProjectId) => (await api.discoverGoogleBigQueryDatasets({projectId: project.id, integrationId: projectIntegration.id, dataProjectId})).map((dataset) => ({...dataset, projectId: dataProjectId}))))).flat();
        setBigQueryDatasets((current) => {
            const currentByKey = new Map(current.map((resource) => [bigQueryDatasetKey(resource), resource]));
            return datasets.map((resource) => ({...currentByKey.get(bigQueryDatasetKey(resource)), ...resource}));
        });
        setSelectedBigQueryDatasets((current) => current.filter((key) => datasets.some((resource) => `${resource.projectId}:${resource.datasetId}` === key)));
        return datasets;
    };

    const loadBigQueryProjects = async () => {
        const projects = await api.discoverGoogleBigQueryProjects({projectId: project.id, integrationId: projectIntegration.id});
        setBigQueryProjects(projects);
        const currentProjects = selectedBigQueryProjects.filter((id) => projects.some((resource) => resource.projectId === id));
        setSelectedBigQueryProjects(currentProjects);
        if (currentProjects.length > 0) return {projects, datasets: await loadBigQueryDatasets(currentProjects)};
        else {
            setBigQueryDatasets([]);
            setSelectedBigQueryDatasets([]);
            return {projects, datasets: []};
        }
    };

    const discoverBigQueryProjects = () => withResourceError(loadBigQueryProjects);

    const refreshBigQueryProjectsAndPendingRecipes = () => withResourceError(async () => {
        const discovery = await loadBigQueryProjects();
        const hasPendingRecipes = (projectIntegration.providerResources ?? []).some((resource) => resource.bigQueryRecipeStatus === GoogleDataProviderResourceDataBigQueryRecipeStatusEnum.PendingEventsExport);
        if (hasPendingRecipes) {
            bigQueryDraftOnNextHydration.current = reconcileBigQueryDraftWithDiscovery({
                selectedProjects: selectedBigQueryProjects,
                selectedDatasets: selectedBigQueryDatasets,
                propertyTimeZones: bigQueryPropertyTimeZones,
            }, discovery.projects, discovery.datasets);
        }
        await revalidatePendingBigQueryRecipes(
            api,
            {projectId: project.id, integrationId: projectIntegration.id},
            projectIntegration.providerResources ?? [],
            GoogleDataProviderResourceDataBigQueryRecipeStatusEnum.PendingEventsExport,
            () => reloadIntegrationDetailWithDraftCleanup(reloadIntegrationDetail, () => {
                bigQueryDraftOnNextHydration.current = null;
            }),
        );
        setUsageLoading(true);
        try {
            setUsage(await loadGoogleIntegrationUsage(api, {projectId: project.id, integrationId: projectIntegration.id}));
        } catch {
            setUsage(null);
        } finally {
            setUsageLoading(false);
        }
    });

    const selectBigQueryProjects = (dataProjectIds) => withResourceError(async () => {
        setSelectedBigQueryProjects(dataProjectIds);
        await loadBigQueryDatasets(dataProjectIds);
    });

    const selectBigQueryDatasets = (datasetKeys) => {
        setBigQueryPropertyTimeZones((current) => withDefaultBigQueryPropertyTimeZones(current, datasetKeys));
        setSelectedBigQueryDatasets(datasetKeys);
    };

    const refreshProviderResources = () => {
        switch (brand.googleDataType) {
            case 'merchant':
                return discoverMerchantAccounts();
            case 'analytics':
                return discoverAnalyticsProperties();
            case 'search-console':
                return discoverSearchConsoleSites();
            case 'bigquery':
                return discoverBigQueryProjects();
            default:
                return Promise.resolve(null);
        }
    };

    useEffect(() => {
        const currentUrl = new URL(window.location.href);
        if (
            automaticDiscoveryStarted.current
            || currentUrl.searchParams.get('googleOAuth') !== 'connected'
            || !projectIntegration?.authorizationSuccessful
        ) {
            return;
        }

        automaticDiscoveryStarted.current = true;
        void refreshProviderResources().finally(() => {
            currentUrl.searchParams.delete('googleOAuth');
            window.history.replaceState(window.history.state, '', currentUrl.toString());
        });
    }, [projectIntegration?.id, projectIntegration?.authorizationSuccessful, brand.googleDataType]);

    useEffect(() => {
        if (
            brand.googleDataType !== 'bigquery'
            || !projectIntegration?.authorizationSuccessful
            || automaticDiscoveryStarted.current
            || bigQueryProjects.length > 0
        ) {
            return;
        }
        automaticDiscoveryStarted.current = true;
        void discoverBigQueryProjects();
    }, [brand.googleDataType, projectIntegration?.id, projectIntegration?.authorizationSuccessful]);

    useEffect(() => {
        if (!projectIntegration?.id) return;
        let active = true;
        setUsageLoading(true);
        loadGoogleIntegrationUsage(api, {projectId: project.id, integrationId: projectIntegration.id})
            .then((data) => {
                if (active) setUsage(data);
            })
            .catch(() => {
                if (active) setUsage(null);
            })
            .finally(() => {
                if (active) setUsageLoading(false);
            });
        return () => { active = false; };
    }, [api, project.id, projectIntegration?.id, projectIntegration?.lastRegularUpdate]);

    const saveBigQueryRecipe = () => withResourceError(async () => {
        const cap = megabytesToBytes(maximumMegabytesBilled);
        const dailyLimit = megabytesToBytes(dailyMegabytesLimit);
        const monthlyLimit = megabytesToBytes(monthlyMegabytesLimit);
        const inputs = selectedBigQueryRecipeInputs(bigQueryDatasets, selectedBigQueryDatasets, cap, dailyLimit, monthlyLimit, bigQueryPropertyTimeZones);
        if (inputs.length === 0 || !Number.isSafeInteger(cap) || cap <= 0) return;
        const results = [];
        for (const input of inputs) {
            results.push(await api.validateAndSelectGoogleBigQueryGA4ExportRecipe({
                projectId: project.id,
                integrationId: projectIntegration.id,
                googleBigQueryRecipeInput: input,
            }));
        }
        await reloadIntegrationDetail();
        const summary = summarizeBigQueryRecipeValidationResults(
            results,
            GoogleBigQueryRecipeValidationDataStatusEnum.PendingEventsExport,
            GoogleBigQueryRecipeValidationDataStatusEnum.Ready,
        );
        const outcome = bigQueryRecipeSaveOutcome(summary);
        if (outcome === 'mixed') {
            const mixedTranslationValues = bigQueryMixedSelectionTranslationValues(summary);
            toast.success(t('google_bigquery.selection_saved_mixed', {
                defaultValue: 'Výběr byl uložen. Aktivováno: {{readyCount}}, čeká na export: {{pendingCount}}. Dry-run odhad aktivovaných datasetů: {{estimatedBytesProcessed}} B.',
                readyCount: mixedTranslationValues.readyCount,
                pendingCount: mixedTranslationValues.pendingCount,
                estimatedBytesProcessed: mixedTranslationValues.estimatedBytesProcessed,
            }));
        } else if (outcome === 'pending') {
            toast.success(t('google_bigquery.selection_saved_pending', {defaultValue: 'Výběr byl uložen. Dataset čeká na dostupný export událostí GA4.'}));
        } else if (outcome === 'ready' && summary.readyEstimatedBytes !== null) {
            toast.success(t('google_bigquery.recipes_saved', {defaultValue: 'GA4 exporty byly ověřeny a aktivovány: {{count}}. Souhrnný dry-run odhad: {{bytes}} B.', count: summary.readyCount, bytes: summary.readyEstimatedBytes}));
        } else {
            toast.success(t('google_bigquery.selection_saved', {defaultValue: 'Výběr BigQuery datasetů byl uložen.'}));
        }
    });

    if (!projectIntegration) {
        return <LoadingComponent loading={loading}>
            {error ? <>
                <FormError error={error}/>
                <div className={'mt-4 flex justify-center'}>
                    <IntegrationConnectButton logo={googleLogo} logoAlt={'Google'} onClick={installAndConnect}/>
                </div>
            </> : <div>
                <Spinner size={'spinner-large'} className={'mx-auto'}/>
                <p className={'mt-4 text-center'}>{t('redirecting')}...</p>
            </div>}
        </LoadingComponent>;
    }

    // A provider fetch/query can fail while the OAuth grant remains valid. Only
    // the explicit authorization flag may turn this control into a reconnect.
    const reconnectRequired = !projectIntegration.authorizationSuccessful;
    const refreshResourcesTitle = t('project.integration-refresh-resources-help', {defaultValue: 'Znovu načte aktuálně dostupné zdroje z Googlu. Váš uložený výběr nezmění.'});
    const resourcesLoadingText = t('project.integration-loading-resources', {defaultValue: 'Načítám aktuálně dostupné zdroje…'});
    const loadingMessage = () => resourcesLoadingText;
    const merchantOptions = merchantAccounts.map((account) => ({
        value: account.accountId,
        label: `[${account.accountId}] ${account.displayName}${account.testAccount ? ' (test)' : ''}`,
    }));
    const analyticsOptions = analyticsPropertySelectOptions(analyticsProperties);
    const searchConsoleOptions = searchConsoleSites
        .filter((site) => site.permissionLevel !== 'siteUnverifiedUser')
        .map((site) => ({
            value: site.siteUrl,
            label: `[${site.siteUrl}] ${site.siteUrl.startsWith('sc-domain:') ? site.siteUrl.slice('sc-domain:'.length) : site.siteUrl}`,
        }));
    const bigQueryProjectOptions = bigQueryProjectSelectOptions(bigQueryProjects);
    const bigQueryDatasetOptions = bigQueryDatasetSelectOptions(bigQueryDatasets);
    const hasSelectedProviderResources = storedResources.length > 0;

    return <LoadingComponent loading={loading}>
        {error && <FormError error={error}/>}
        <div className={'flex flex-col gap-5'}>
            <IntegrationSection
                title={t('project.integration-oauth-title', {defaultValue: 'Připojení k {{name}}', name: brand.name})}
                description={t('project.integration-oauth-help', {defaultValue: 'OAuth připojení slouží k bezpečnému načtení dostupných zdrojů. Obnovení připojení nezmění uložený výběr.'})}
            >
                <OAuthConnectionStatus
                    connected={!reconnectRequired}
                    action={<IntegrationConnectButton logo={googleLogo} logoAlt={'Google'} label={reconnectRequired ? t('project.integration-connect', {defaultValue: 'Připojit'}) : t('project.integration-reconnect', {defaultValue: 'Obnovit připojení'})} onClick={reconnect}/>}
                />
                <div className={'mt-3 flex flex-wrap gap-2'}>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${hasSelectedProviderResources ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'}`}>
                        {hasSelectedProviderResources ? t('project.integration-source-selected', {defaultValue: 'Zdroj vybrán'}) : t('project.integration-source-not-selected', {defaultValue: 'Zdroj není vybrán'})}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${projectIntegration.readyForScheduler ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'}`}>
                        {projectIntegration.readyForScheduler ? t('project.integration-scheduler-ready', {defaultValue: 'Automatické stahování připraveno'}) : t('project.integration-scheduler-not-ready', {defaultValue: 'Automatické stahování není připraveno'})}
                    </span>
                </div>
            </IntegrationSection>

            {projectIntegration.authorizationSuccessful && <IntegrationSection
                title={t('project.integration-usage-title', {defaultValue: 'Využití integrace'})}
                description={t('project.integration-usage-help', {defaultValue: 'Nízkorežijní audit ukazuje souhrn práce integrace. U BigQuery odpovídá zpracovaný objem dat podkladu pro účtování Google Cloud; u ostatních integrací slouží hlavně k provozní diagnostice.'})}
            >
                {usageLoading ? <ResourceLoadingNotice>{t('project.integration-loading-usage', {defaultValue: 'Načítám využití integrace…'})}</ResourceLoadingNotice> : usage && <>
                    {(usage.dailyLimitExhausted || usage.monthlyLimitExhausted) && <div className={'mb-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100'}>
                        <p className={'font-semibold'}>{t('google_bigquery.budget_exhausted_title', {defaultValue: 'Stahování BigQuery je pozastavené limitem'})}</p>
                        <p className={'mt-1'}>{usage.dailyLimitExhausted
                            ? t('google_bigquery.daily_budget_exhausted', {defaultValue: 'Byl vyčerpán dnešní limit. Automatické stahování se obnoví následující den.'})
                            : t('google_bigquery.monthly_budget_exhausted', {defaultValue: 'Byl vyčerpán měsíční limit. Automatické stahování se obnoví v příštím kalendářním měsíci.'})}</p>
                    </div>}
                    <div className={'grid gap-3 sm:grid-cols-3'}>
                        {[
                            [t('project.integration-usage-today', {defaultValue: 'Dnes'}), usage.today],
                            [t('project.integration-usage-current-month', {defaultValue: 'Tento měsíc'}), usage.currentMonth],
                            [t('project.integration-usage-previous-month', {defaultValue: 'Minulý měsíc'}), usage.previousMonth],
                        ].map(([label, period]) => <div key={label} className={'rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40'}>
                            <p className={'text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400'}>{label}</p>
                            <p className={'mt-1 text-lg font-semibold text-slate-900 dark:text-white'}>{brand.googleDataType === 'bigquery' ? formatProcessedBytes(period?.bytesProcessed, i18n.resolvedLanguage ?? i18n.language) : Number(period?.rowsPublished ?? 0).toLocaleString()}</p>
                            <p className={'mt-1 text-xs text-slate-500 dark:text-slate-400'}>{brand.googleDataType === 'bigquery'
                                ? t('project.integration-usage-processed', {defaultValue: 'zpracováno poskytovatelem'})
                                : t('project.integration-usage-rows', {defaultValue: 'publikovaných řádků'})}</p>
                        </div>)}
                    </div>
                </>}
            </IntegrationSection>}

            {brand.googleDataType === 'merchant' && projectIntegration.authorizationSuccessful && <IntegrationSection
                title={t('google_merchant.accounts_title', {defaultValue: 'Merchant účty'})}
                description={t('google_merchant.accounts_help', {defaultValue: 'Každý účet se plánuje a vyhodnocuje samostatně. Aktualizace pouze znovu načte dostupné účty z Googlu a uložený výběr nezmění.'})}
            >
                {resourceLoading && <ResourceLoadingNotice>{resourcesLoadingText}</ResourceLoadingNotice>}
                <Select
                    isMulti
                    isLoading={resourceLoading}
                    name={'google-merchant-accounts'}
                    options={merchantOptions}
                    value={merchantOptions.filter((option) => selectedAccounts.includes(option.value))}
                    onChange={(options) => setSelectedAccounts(options.map((option) => option.value))}
                    className={'basic-multi-select'}
                    classNamePrefix={'select'}
                    placeholder={null}
                    loadingMessage={loadingMessage}
                    noOptionsMessage={() => t('project.integration-no-options')}
                    isDisabled={resourceLoading}
                />
                <IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button'} title={refreshResourcesTitle} disabled={resourceLoading} onClick={discoverMerchantAccounts}>{t('project.integration-refresh-list', {defaultValue: 'Aktualizovat seznam'})}</Button>}>
                    <Button className={'button-primary'} disabled={merchantOptions.length === 0 || resourceLoading} onClick={saveMerchantAccounts}>{t('project.integration-save-selection', {defaultValue: 'Uložit výběr'})}</Button>
                </IntegrationActions>
            </IntegrationSection>}

            {brand.googleDataType === 'analytics' && projectIntegration.authorizationSuccessful && <IntegrationSection
                title={t('google_analytics.properties_title', {defaultValue: 'GA4 služby'})}
                description={t('google_analytics.properties_help', {defaultValue: 'Vyberte GA4 služby, které má Datixo pravidelně stahovat. Časová zóna a měna každé služby se převezmou přímo z Google Analytics. Aktualizace seznamu uložený výběr nezmění.'})}
            >
                {resourceLoading && <ResourceLoadingNotice>{resourcesLoadingText}</ResourceLoadingNotice>}
                <Select
                    isMulti
                    isLoading={resourceLoading}
                    name={'google-analytics-properties'}
                    options={analyticsOptions}
                    value={analyticsOptions.filter((option) => selectedProperties.includes(option.value))}
                    onChange={(options) => setSelectedProperties(options.map((option) => option.value))}
                    className={'basic-multi-select'}
                    classNamePrefix={'select'}
                    placeholder={null}
                    loadingMessage={loadingMessage}
                    noOptionsMessage={() => t('project.integration-no-options')}
                    isDisabled={resourceLoading}
                />
                <div className={'mt-4'}><IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button'} title={refreshResourcesTitle} disabled={resourceLoading} onClick={discoverAnalyticsProperties}>{t('project.integration-refresh-list', {defaultValue: 'Aktualizovat seznam'})}</Button>}>
                    <Button className={'button-primary'} disabled={analyticsOptions.length === 0 || resourceLoading} onClick={saveAnalyticsProperties}>{t('project.integration-save-selection', {defaultValue: 'Uložit výběr'})}</Button>
                </IntegrationActions></div>
            </IntegrationSection>}

            {brand.googleDataType === 'search-console' && projectIntegration.authorizationSuccessful && <IntegrationSection
                title={t('google_search_console.domains_title', {defaultValue: 'Domény v Search Console'})}
                description={t('google_search_console.domains_help', {defaultValue: 'Vyberte domény, které má Datixo pravidelně stahovat. Pokud vybraná doména v projektu ještě není, při uložení se bezpečně vytvoří. Aktualizace seznamu uložený výběr nezmění.'})}
            >
                {resourceLoading && <ResourceLoadingNotice>{resourcesLoadingText}</ResourceLoadingNotice>}
                <Select
                    isMulti
                    isLoading={resourceLoading}
                    name={'google-search-console-sites'}
                    options={searchConsoleOptions}
                    value={searchConsoleOptions.filter((option) => selectedSites.includes(option.value))}
                    onChange={(options) => setSelectedSites(options.map((option) => option.value))}
                    className={'basic-multi-select'}
                    classNamePrefix={'select'}
                    placeholder={null}
                    loadingMessage={loadingMessage}
                    isDisabled={resourceLoading}
                    noOptionsMessage={() => t('project.integration-no-options')}
                />
                {!resourceLoading && searchConsoleOptions.length === 0 && <div className={'mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}>
                    <span className={'mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-500'} aria-hidden={'true'}/>
                    <p className={'leading-5'}>{t('google_search_console.resources_unavailable', {defaultValue: 'Uložený seznam domén není dostupný. Aktualizujte seznam a výběr znovu uložte.'})}</p>
                </div>}
                <div className={searchConsoleOptions.length === 0 ? 'mt-3' : 'mt-4'}>
                    <IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button'} title={refreshResourcesTitle} disabled={resourceLoading} onClick={discoverSearchConsoleSites}>{t('project.integration-refresh-list', {defaultValue: 'Aktualizovat seznam'})}</Button>}>
                        <Button className={'button-primary'} disabled={searchConsoleOptions.length === 0 || resourceLoading} onClick={saveSearchConsoleSites}>{t('project.integration-save-selection', {defaultValue: 'Uložit výběr'})}</Button>
                    </IntegrationActions>
                </div>
            </IntegrationSection>}

            {brand.googleDataType === 'bigquery' && projectIntegration.authorizationSuccessful && <IntegrationSection
                title={t('google_bigquery.resources', {defaultValue: 'BigQuery projekt a dataset'})}
                description={t('google_bigquery.resources_help_with_refresh', {defaultValue: 'Discovery je read-only. Dataset se aktivuje až po validaci GA4 export schématu, dry-run odhadu a nastavení maximálního objemu účtovaných dat. Aktualizace seznamu uložený výběr nezmění.'})}
            >
                {resourceLoading && <ResourceLoadingNotice>{resourcesLoadingText}</ResourceLoadingNotice>}
                <label className={'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200'}>{t('google_bigquery.project', {defaultValue: 'Google Cloud projekt'})}</label>
                <Select isMulti isLoading={resourceLoading} name={'google-bigquery-project'} options={bigQueryProjectOptions} value={bigQueryProjectOptions.filter((option) => selectedBigQueryProjects.includes(option.value))} onChange={(options) => selectBigQueryProjects(options.map((option) => option.value))} className={'basic-multi-select'} classNamePrefix={'select'} placeholder={null} loadingMessage={loadingMessage} noOptionsMessage={() => t('project.integration-no-options')} isDisabled={resourceLoading}/>
                <label className={'mb-1 mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200'}>{t('google_bigquery.dataset', {defaultValue: 'GA4 export dataset'})}</label>
                <Select isMulti
                    isLoading={resourceLoading}
                    name={'google-bigquery-dataset'}
                    options={bigQueryDatasetOptions}
                    value={bigQueryDatasetOptions.filter((option) => selectedBigQueryDatasets.includes(option.value))}
                    onChange={(options) => selectBigQueryDatasets(options.map((option) => option.value))}
                    className={'basic-multi-select'}
                    classNamePrefix={'select'}
                    placeholder={null}
                    loadingMessage={loadingMessage}
                    isDisabled={selectedBigQueryProjects.length === 0 || resourceLoading}
                    noOptionsMessage={() => t('project.integration-no-options')}
                />
                {selectedBigQueryDatasets.map((datasetKey) => {
                    const dataset = bigQueryDatasets.find((resource) => bigQueryDatasetKey(resource) === datasetKey);
                    if (dataset?.bigQueryRecipeStatus !== GoogleDataProviderResourceDataBigQueryRecipeStatusEnum.PendingEventsExport) return null;
                    return <div key={`pending-${datasetKey}`} className={'mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'} role={'status'}>
                        <p className={'font-semibold'}>{t('google_bigquery.events_export_pending_title', {defaultValue: 'Export událostí GA4 zatím čeká na data'})}</p>
                        <p className={'mt-1 leading-5'}>{t('google_bigquery.events_export_pending_help', {defaultValue: 'Vybraný dataset je uložený, ale zatím neobsahuje events_YYYYMMDD ani events_intraday_YYYYMMDD. Zkontrolujte zapnutý Daily nebo Streaming export a později seznam obnovte.'})}</p>
                    </div>;
                })}
                {selectedBigQueryDatasets.length > 0 && <>
                    <div className={'mt-4 space-y-4'}>
                        {selectedBigQueryDatasets.map((datasetKey) => {
                            const dataset = bigQueryDatasets.find((resource) => bigQueryDatasetKey(resource) === datasetKey);
                            if (!dataset) return null;
                            const propertyTimeZone = bigQueryPropertyTimeZones[datasetKey] ?? null;
                            const inputId = `google-bigquery-property-timezone-${datasetKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                            return <div key={datasetKey}>
                                <label className={'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200'} htmlFor={inputId}>
                                    {t('google_bigquery.user_enrichment_property_timezone', {defaultValue: 'Časová zóna GA4 služby pro {{dataset}}', dataset: `${dataset.projectId}.${dataset.datasetId}`})}
                                </label>
                                <Select
                                    inputId={inputId}
                                    name={`google-bigquery-property-timezone-${datasetKey}`}
                                    options={timeZoneOptions}
                                    value={propertyTimeZone ? {value: propertyTimeZone, label: propertyTimeZone} : null}
                                    onChange={(option) => setBigQueryPropertyTimeZones((current) => ({...current, [datasetKey]: option?.value ?? null}))}
                                    className={'basic-single-select'}
                                    classNamePrefix={'select'}
                                    placeholder={t('google_bigquery.user_enrichment_property_timezone_placeholder', {defaultValue: 'Vyberte IANA časovou zónu'})}
                                    noOptionsMessage={() => t('project.integration-no-options')}
                                    isClearable
                                    isDisabled={resourceLoading}
                                />
                                <p className={`mt-1 text-xs leading-5 ${propertyTimeZone ? 'text-slate-500 dark:text-slate-400' : 'text-amber-700 dark:text-amber-300'}`}>
                                    {propertyTimeZone
                                        ? t('google_bigquery.user_enrichment_property_timezone_configured', {defaultValue: 'Použije se pro denní agregace pseudonymních identifikátorů; umístění datasetu časovou zónu nenahrazuje.'})
                                        : t('google_bigquery.user_enrichment_property_timezone_required', {defaultValue: 'Pro obohacení z pseudonymous_users_* je časová zóna povinná. Stávající zpracování events_* zůstane dostupné i bez ní.'})}
                                </p>
                            </div>;
                        })}
                    </div>
                    <label className={'mb-1 mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200'} htmlFor={'google-bigquery-byte-cap'}>{t('google_bigquery.maximum_megabytes', {defaultValue: 'Limit dat na jeden dotaz'})}</label>
                    <div className={'relative'}>
                        <input id={'google-bigquery-byte-cap'} type={'text'} inputMode={'numeric'} value={maximumMegabytesBilled} onChange={(event) => setMaximumMegabytesBilled(formatMegabytesInput(event.target.value))} className={'w-full rounded border border-slate-300 bg-white py-2 pl-3 pr-14 text-sm dark:border-slate-700 dark:bg-slate-900'}/>
                        <span className={'pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-500 dark:text-slate-400'}>MB</span>
                    </div>
                    <div className={'mt-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-slate-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-slate-200'}>
                        <p className={'font-semibold text-slate-900 dark:text-white'}>{t('google_bigquery.cost_limit_recommendation', {defaultValue: 'Doporučujeme začít s 1 000 MB.'})}</p>
                        <p className={'mt-1 leading-5'}>{t('google_bigquery.cost_limit_explanation', {defaultValue: 'Je to bezpečnostní strop pro jeden dotaz, nikoliv měsíční rozpočet ani objem uložených dat. Datixo nejprve zdarma odhadne velikost dotazu. Pokud by limit překročil, dotaz se nespustí a Google jej neúčtuje.'})}</p>
                        <p className={'mt-1 leading-5'}>{t('google_bigquery.cost_limit_price', {defaultValue: 'Při běžném on-demand účtování odpovídá 1 000 MB po vyčerpání bezplatného měsíčního limitu částce nižší než 1 americký cent za dotaz. Skutečná cena závisí na vašem tarifu, regionu a měně v Google Cloud.'})}</p>
                    </div>
                    <div className={'mt-4 grid gap-4 sm:grid-cols-2'}>
                        <div>
                            <label className={'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200'} htmlFor={'google-bigquery-daily-cap'}>{t('google_bigquery.daily_limit', {defaultValue: 'Denní limit'})}</label>
                            <div className={'relative'}><input id={'google-bigquery-daily-cap'} type={'text'} inputMode={'numeric'} value={dailyMegabytesLimit} onChange={(event) => setDailyMegabytesLimit(formatMegabytesInput(event.target.value))} className={'w-full rounded border border-slate-300 bg-white py-2 pl-3 pr-14 text-sm dark:border-slate-700 dark:bg-slate-900'}/><span className={'pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-500'}>MB</span></div>
                        </div>
                        <div>
                            <label className={'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200'} htmlFor={'google-bigquery-monthly-cap'}>{t('google_bigquery.monthly_limit', {defaultValue: 'Měsíční limit'})}</label>
                            <div className={'relative'}><input id={'google-bigquery-monthly-cap'} type={'text'} inputMode={'numeric'} value={monthlyMegabytesLimit} onChange={(event) => setMonthlyMegabytesLimit(formatMegabytesInput(event.target.value))} className={'w-full rounded border border-slate-300 bg-white py-2 pl-3 pr-14 text-sm dark:border-slate-700 dark:bg-slate-900'}/><span className={'pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-500'}>MB</span></div>
                        </div>
                    </div>
                    <p className={'mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400'}>{t('google_bigquery.period_limits_help', {defaultValue: 'Po dosažení denního nebo měsíčního limitu Datixo další placené dotazy nespustí. Limity se vztahují na celou integraci a součet všech vybraných datasetů; ruční změna limitu stahování znovu povolí pouze tehdy, když je nový limit vyšší než již zpracovaný objem.'})}</p>
                </>}
                <div className={'mt-4'}><IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button'} title={refreshResourcesTitle} disabled={resourceLoading} onClick={refreshBigQueryProjectsAndPendingRecipes}>{t('project.integration-refresh-list', {defaultValue: 'Aktualizovat seznam'})}</Button>}>
                    <Button className={'button-primary'} disabled={selectedBigQueryDatasets.length === 0 || resourceLoading} onClick={saveBigQueryRecipe}>{t('google_bigquery.save_selection', {defaultValue: 'Uložit výběr'})}</Button>
                </IntegrationActions></div>
            </IntegrationSection>}
        </div>
    </LoadingComponent>;
}
